import { clamp, isEqual } from 'lodash-es';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { filter, fromEvent, tap } from 'rxjs';

import { useCustomEventEmitter, useSubscriptions } from '@/hook';

import { TRACK_TYPE, type VBaseTrackProps, useVBaseTrack } from '../VBaseTrack';
import {
  FRAME_SLIDER_SCALE,
  FRAME_SLIDER_SCALE_CONFIG,
} from '../VFrameSlider/constant';
import { alignThumbToCenter, getStepConfig } from '../VFrameSlider/util';
import { useVSliderContext } from '../VSlider/hook';
import { makeNormalizedValue } from '../VSlider/util';
import { VFrameTrackSliderContext } from './context';
import type {
  FrameTrackSliderRange,
  VFrameTrackSliderEventHandlerMap,
  VFrameTrackSliderProps,
} from './type';

type VFrameTrackSliderProviderProps = PropsWithChildren<
  Required<Pick<VFrameTrackSliderProps, 'value' | 'scale'>> &
    Pick<
      VFrameTrackSliderProps,
      | 'autoTracking'
      | 'cachedRange'
      | 'highlightedRange'
      | 'preloadedRange'
      | 'trackRange'
      | 'variant'
      | 'onCachedRangeChange'
      | 'onChange'
      | 'onHighlightedRangeChange'
      | 'onPreloadedRangeChange'
      | 'onScaleChange'
      | 'onTrackRangeChange'
    >
>;

export const VFrameTrackSliderContextProvider = ({
  children,

  autoTracking,
  cachedRange: initialCachedRange = null,
  highlightedRange: initialHighlightedRange = null,
  preloadedRange: initialPreloadRange = null,
  scale: initialScale,
  trackRange: initialTrackRange = null,
  value: initialValue,
  variant,
  onCachedRangeChange,
  onChange,
  onHighlightedRangeChange,
  onPreloadedRangeChange,
  onTrackRangeChange,
  onScaleChange,
}: VFrameTrackSliderProviderProps) => {
  const { step, setStep } = useVBaseTrack();
  const { disabled, max, min, readOnly, $container } = useVSliderContext();

  const [value, _setValue] = useState(initialValue);
  const [cachedRange, _setCachedRange] =
    useState<Nullish<FrameTrackSliderRange>>(initialCachedRange);
  const [highlightedRange, _setHighlightedRange] = useState<
    Nullish<FrameTrackSliderRange>
  >(initialHighlightedRange);
  const [preloadedRange, _setPreloadRange] =
    useState<Nullish<FrameTrackSliderRange>>(initialPreloadRange);
  const [trackRange, _setTrackRange] =
    useState<Nullish<FrameTrackSliderRange>>(initialTrackRange);
  const [scale, _setScale] = useState<FRAME_SLIDER_SCALE>(initialScale);
  const normalizedCachedRangeValue = makeNormalizedValue({
    type: TRACK_TYPE.MULTIPLE_RANGE,
    value: cachedRange as VBaseTrackProps<TRACK_TYPE.MULTIPLE_RANGE>['value'],
    min,
    step,
  });
  const $thumbRef = useRef<HTMLDivElement>(null);

  const size = max - min + 1;

  const { eventEmitter } =
    useCustomEventEmitter<VFrameTrackSliderEventHandlerMap>({
      onCachedRangeChange: onCachedRangeChange ?? (() => {}),
      onChange: onChange ?? (() => {}),
      onHighlightedRangeChange: onHighlightedRangeChange ?? (() => {}),
      onPreloadedRangeChange: onPreloadedRangeChange ?? (() => {}),
      onTrackRangeChange: onTrackRangeChange ?? (() => {}),
      onScaleChange: onScaleChange ?? (() => {}),
    });

  const setValue = (
    newValue: number,
    trigger?: 'thumb' | 'track' | 'marker',
  ) => {
    _setValue(newValue);

    if (onChange) {
      eventEmitter.current.emit('change', { value: newValue }, { trigger });
    }
  };

  const setTrackRange = (trackRange: FrameTrackSliderRange) => {
    _setTrackRange(trackRange);

    if (onTrackRangeChange) {
      eventEmitter.current.emit('trackRangeChange', { value: trackRange });
    }
  };

  const setCachedRange = (cachedRange: FrameTrackSliderRange) => {
    _setCachedRange(cachedRange);

    if (onCachedRangeChange) {
      eventEmitter.current.emit('cachedRangeChange', { value: cachedRange });
    }
  };

  const setHighlightedRange = (highlightedRange: FrameTrackSliderRange) => {
    _setHighlightedRange(highlightedRange);

    if (onHighlightedRangeChange) {
      eventEmitter.current.emit('highlightedRangeChange', {
        value: highlightedRange,
      });
    }
  };

  const setPreloadRange = (preloadedRange: FrameTrackSliderRange) => {
    _setPreloadRange(preloadedRange);

    if (onPreloadedRangeChange) {
      eventEmitter.current.emit('preloadedRangeChange', {
        value: preloadedRange,
      });
    }
  };

  const setScale = (newScale: FRAME_SLIDER_SCALE) => {
    let timelineScaleThreshold = FRAME_SLIDER_SCALE.SCALE_1;

    for (const key in FRAME_SLIDER_SCALE_CONFIG) {
      const k = +key as FRAME_SLIDER_SCALE;
      const { threshold } = FRAME_SLIDER_SCALE_CONFIG[k];

      if (size >= threshold) timelineScaleThreshold = k;
    }

    newScale = clamp(
      newScale,
      FRAME_SLIDER_SCALE.SCALE_1,
      timelineScaleThreshold,
    ) as FRAME_SLIDER_SCALE;

    _setScale(newScale);

    if (onScaleChange) {
      eventEmitter.current.emit('scaleChange', { scale: newScale });
    }
  };

  const contextValue = {
    cachedRange,
    disabled,
    eventEmitter: eventEmitter.current,
    highlightedRange,
    normalizedCachedRangeValue,
    max,
    min,
    preloadedRange,
    readOnly,
    scale,
    trackRange,
    value,
    variant,
    $thumbRef,

    setCachedRange,
    setHighlightedRange,
    setPreloadRange,
    setScale,
    setTrackRange,
    setValue,
  };

  useEffect(() => {
    setStep(getStepConfig(scale).step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  useSubscriptions(
    () => [
      fromEvent<WheelEvent>(window, 'wheel', { passive: false })
        .pipe(
          filter(
            e =>
              (e.target === $container.current ||
                (e.target as HTMLElement).closest('[role="slider"]') ===
                  $container.current) &&
              !e.shiftKey,
          ),
          tap(e => {
            e.preventDefault();
            setScale(
              (e.deltaY > 0 ? scale + 1 : scale - 1) as FRAME_SLIDER_SCALE,
            );
            setTimeout(() => alignThumbToCenter({ $container, $thumbRef }), 32);
          }),
        )
        .subscribe(),
    ],
    [scale],
  );

  useEffect(() => {
    if (!autoTracking) return;

    alignThumbToCenter({ $container, $thumbRef });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTracking, max, min, scale, value]);

  useEffect(() => {
    if (initialValue !== value) setValue(initialValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  useEffect(() => {
    if (isEqual(initialCachedRange, cachedRange)) return;

    setCachedRange(initialCachedRange ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCachedRange]);

  useEffect(() => {
    if (isEqual(initialHighlightedRange, highlightedRange)) return;

    setHighlightedRange(initialHighlightedRange ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHighlightedRange]);

  useEffect(() => {
    if (isEqual(initialPreloadRange, preloadedRange)) return;

    setPreloadRange(initialPreloadRange ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPreloadRange]);

  return (
    <VFrameTrackSliderContext.Provider value={contextValue}>
      {children}
    </VFrameTrackSliderContext.Provider>
  );
};

VFrameTrackSliderContextProvider.displayName =
  'VFrameTrackSliderContextProvider';
